import * as B from '@blueprintjs/core';
import * as I from 'immutable';
import React from 'react';

import {ApiFeature} from 'app/modules/Remote/Feature';
import {
  ApiFeatureCollection,
  HydratedFeatureCollection,
} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganization, ApiOrganizationAreaUnit} from 'app/modules/Remote/Organization';
import {ApiProject} from 'app/modules/Remote/Project';
import {useBatchUpdateNotes} from 'app/pages/Agent/hooks';
import {NoteQueue} from 'app/pages/Agent/NoteQueue';
import {ObservationMap} from 'app/pages/Agent/ObservationMap';
import {LoggedInAuth, useAuth} from 'app/providers/AuthProvider';
import {FeatureDataProvider, useFeatureDataForFeature} from 'app/providers/FeatureDataProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import {NOTE_TYPE_AGENT, NOTE_TYPE_USER} from 'app/utils/constants';
import featureFlags, {useUserInfo} from 'app/utils/featureFlags';
import * as featureUtils from 'app/utils/featureUtils';
import {makeAreaDisplayString} from 'app/utils/geoJsonUtils';
import {useStateWithDeps} from 'app/utils/hookUtils';
import * as imageryUtils from 'app/utils/imageryUtils';
import * as layerUtils from 'app/utils/layerUtils';
import {makeImageRefsFromNote, scrollToFocusedNote} from 'app/utils/noteUtils';

import {OpenAIRunLogLink} from './DataConfigDisplay';
import cs from './styles.module.scss';
import {ApiAgentNote} from './types';
import {isNoteDismissed, isNoteSaved} from './utils';

export const ReviewObservationsWrapper = ({
  noteQueue,
  features,
  runId,
  focusNote,
  focusedNoteId,
  areaUnit,
  selectedNoteIds,
  toggleNoteSelected,
}: {
  noteQueue: ApiAgentNote[];
  features: ApiFeature[];
  runId: string;
  focusNote: (noteId: string | number) => void;
  focusedNoteId: string | null;
  areaUnit: ApiOrganizationAreaUnit;
  selectedNoteIds: ReadonlySet<string>;
  toggleNoteSelected: (noteId: string | number) => void;
}) => {
  const [currentIndex, setCurrentIndex] = React.useState(0);
  const [allowEditingGeometry, setAllowEditingGeometry] = React.useState(false);
  const [showNoteGeometry, setShowNoteGeometry] = React.useState(true);

  const currentNote: ApiAgentNote | null = noteQueue[currentIndex] || null;

  const updateNoteMutation = useBatchUpdateNotes(
    String(currentNote?.properties.workflow_id),
    String(currentNote?.properties.run_id)
  );
  const _patchNote = updateNoteMutation.mutate;
  const [organization, profile] = useUserInfo();
  const patchNote = React.useCallback(
    (note: ApiAgentNote) => {
      // TEMP: This is a saftey measure as we do some testing in user accounts.
      // Only save notes for internal/upstream org
      if (featureFlags.AGENT_IN_NAVBAR(organization, profile)) {
        //batchUpdateNotes takes an array
        _patchNote([note]);
      }
    },
    [organization, profile, _patchNote]
  );

  React.useEffect(() => {
    if (focusedNoteId === null || focusedNoteId === undefined) return;
    const idx = noteQueue.findIndex((n) => String(n.id) === focusedNoteId);
    if (idx !== -1) setCurrentIndex(idx);
  }, [focusedNoteId, noteQueue, setCurrentIndex]);

  const handleNext = React.useCallback(() => {
    const nextIndex = Math.min(noteQueue.length - 1, currentIndex + 1);
    const nextNote = noteQueue[nextIndex];
    focusNote(nextNote.id);
    setCurrentIndex(nextIndex);
  }, [currentIndex, noteQueue, setCurrentIndex, focusNote]);

  const handleDismiss = React.useCallback(
    (noteWithEdits: ApiAgentNote) => {
      // Including noteType: NOTE_TYPE_AGENT in case the user accidentally saved the note
      // instead of dismissed it and wants to reverse the action.
      patchNote({
        ...noteWithEdits,
        isArchived: true,
        noteType: NOTE_TYPE_AGENT,
      });
      handleNext();
    },
    [patchNote, handleNext]
  );

  const handleSave = React.useCallback(
    (noteWithEdits: ApiAgentNote) => {
      // Include isArchived false in case the user accidentally dismissed the note
      // instead of saved it and wants to reverse the action.
      patchNote({...noteWithEdits, noteType: NOTE_TYPE_USER, isArchived: false});
      handleNext();
    },
    [patchNote, handleNext]
  );

  // Define hotkeys
  const hotkeys = React.useMemo(
    () => [
      {
        combo: 'left',
        global: true,
        label: 'Previous note',
        onKeyDown: () =>
          setCurrentIndex((prev) => {
            const newIndex = Math.max(0, prev - 1);
            focusNote(noteQueue[newIndex].id);
            return newIndex;
          }),
        preventDefault: true,
      },
      {
        combo: 'right',
        global: true,
        label: 'Next note',
        onKeyDown: handleNext,
        preventDefault: true,
      },
      {
        combo: 'd',
        global: true,
        label: 'Dismiss note',
        onKeyDown: () => handleDismiss(currentNote),
        preventDefault: true,
      },
      {
        combo: 's',
        global: true,
        label: 'Save note',
        onKeyDown: () => handleSave(currentNote),
        preventDefault: true,
      },
      {
        combo: 'e',
        global: true,
        label: 'Toggle geometry editing',
        onKeyDown: () => setAllowEditingGeometry((prev) => !prev),
        preventDefault: true,
      },
    ],
    [handleNext, handleDismiss, currentNote, handleSave]
  );

  const {handleKeyDown, handleKeyUp} = B.useHotkeys(hotkeys);
  const hotkeyDivRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (hotkeyDivRef.current) {
      hotkeyDivRef.current.focus();
    }
  }, [hotkeyDivRef]);

  const [projects, featureCollectionsById, , _projectsMeta] = useProjects();
  const auth = useAuth();

  const {feature, featureCollection, project} = React.useMemo(() => {
    const feature = features.find((f) => f.id === currentNote?.featureId);
    const featureCollection = featureCollectionsById.get(currentNote?.featureCollectionId);
    const project = projects?.find((p) => p?.get('id') === currentNote?.projectId);
    return {feature, featureCollection, project};
  }, [currentNote, features, featureCollectionsById, projects]);

  const propertyAreaDisplay = React.useMemo(
    () => makeAreaDisplayString(feature?.geometry ?? null, areaUnit),
    [feature, areaUnit]
  );

  if (!feature || !featureCollection || !currentNote || !project) {
    return null;
  }

  return (
    <FeatureDataProvider featureCollectionId={featureCollection.get('id')}>
      <div className={cs.reviewObservationsWrapper}>
        <div className={cs.reviewObservationsHeader}>
          <div className={cs.reviewObservationsPropertyInfo}>
            <B.Icon icon="clean" className={cs.agentText} />
            <span>{feature?.properties?.name}</span>
            {propertyAreaDisplay && <span>{propertyAreaDisplay}</span>}
            <ImageRefDisplay
              note={currentNote}
              featureId={feature.id}
              featureCollection={featureCollection}
              organization={organization}
            />
          </div>
          <div>
            <B.Button
              icon="polygon-filter"
              minimal
              small
              active={showNoteGeometry}
              title={showNoteGeometry ? 'Hide note geometry' : 'Show note geometry'}
              onClick={() => setShowNoteGeometry((prev) => !prev)}
            />
            <B.Button
              icon="key-command"
              minimal
              small
              title="Keyboard Shortcuts"
              onClick={() => {
                document.dispatchEvent(
                  new KeyboardEvent('keydown', {
                    key: '?',
                    shiftKey: true,
                  })
                );
              }}
            />
            <OpenAIRunLogLink
              runId={runId}
              featureId={feature?.id}
              buttonProps={{outlined: false, minimal: true}}
            />
          </div>
        </div>
        <div
          className={cs.reviewObservationsQueueAndMap}
          tabIndex={0}
          onKeyDown={handleKeyDown}
          onKeyUp={handleKeyUp}
          ref={hotkeyDivRef}
        >
          <ReviewObservations
            selectedFeature={I.fromJS(feature)}
            selectedFeatureCollection={featureCollection}
            selectedFeatureIdParams={I.Set([feature?.id.toString() ?? ''])}
            selectedProject={project}
            organization={organization}
            firebaseToken={(auth as LoggedInAuth).firebaseToken}
            noteQueue={noteQueue}
            currentIndex={currentIndex}
            currentNote={currentNote}
            onNoteSelect={(index) => {
              const note = noteQueue[index];
              focusNote(note.id);
              setCurrentIndex(index);
            }}
            features={features}
            onDismiss={handleDismiss}
            onSave={handleSave}
            areaUnit={areaUnit}
            selectedNoteIds={selectedNoteIds}
            toggleNoteSelected={toggleNoteSelected}
            allowEditingGeometry={allowEditingGeometry}
            setAllowEditingGeometry={setAllowEditingGeometry}
            showNoteGeometry={showNoteGeometry}
            setShowNoteGeometry={setShowNoteGeometry}
          />
        </div>
      </div>
    </FeatureDataProvider>
  );
};

/**
 * Labels the note's scenes like DateSelectCard does (operator + sensor name),
 * falling back to the raw source id until feature data has loaded.
 */
const ImageRefDisplay: React.FC<{
  note: ApiAgentNote;
  featureId: number;
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  organization: I.ImmutableOf<ApiOrganization>;
}> = ({note, featureId, featureCollection, organization}) => {
  const featureData = useFeatureDataForFeature(featureId);

  const labels = React.useMemo(
    () =>
      makeImageRefsFromNote(note).map((imageRef) => {
        if (!imageRef.cursor || !imageRef.layerKey) {
          return '';
        }

        const date = imageryUtils.formatDate(imageRef.cursor, imageRef.layerKey);
        const featureDatum =
          featureData &&
          featureUtils.getFeatureDatumFromLayerKey(
            featureData,
            imageRef.layerKey,
            imageRef.cursor,
            featureCollection as unknown as HydratedFeatureCollection
          );
        const sourceDetails =
          featureDatum &&
          featureUtils.imageAndSourceDetails(featureDatum, imageRef.layerKey, organization)
            .sourceDetails;

        if (!sourceDetails?.operator) {
          const {sourceId} = layerUtils.parseLayerKey(imageRef.layerKey);
          return `${sourceId} (${date})`;
        }

        return `${sourceDetails.operator} ${sourceDetails.name} (${date})`;
      }),
    [note, featureData, featureCollection, organization]
  );

  return (
    <>
      {labels.map((label, i) => (
        <span key={i}>{label}</span>
      ))}
    </>
  );
};

const ReviewObservations: React.FC<{
  selectedFeature: I.ImmutableOf<ApiFeature>;
  selectedFeatureCollection: I.ImmutableOf<ApiFeatureCollection>;
  organization: I.ImmutableOf<ApiOrganization>;
  selectedProject: I.ImmutableOf<ApiProject>;
  selectedFeatureIdParams: I.Set<string>;
  noteQueue: ApiAgentNote[];
  currentNote: ApiAgentNote;
  firebaseToken: string;
  currentIndex: number;
  onNoteSelect: (index: number) => void;
  features: ApiFeature[];
  onDismiss: (noteWithEdits: ApiAgentNote) => void;
  onSave: (noteWithEdits: ApiAgentNote) => void;
  areaUnit: ApiOrganizationAreaUnit;
  selectedNoteIds: ReadonlySet<string>;
  toggleNoteSelected: (noteId: string | number) => void;
  allowEditingGeometry: boolean;
  setAllowEditingGeometry: React.Dispatch<React.SetStateAction<boolean>>;
  showNoteGeometry: boolean;
  setShowNoteGeometry: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({
  selectedFeature,
  selectedFeatureCollection,
  organization,
  noteQueue,
  currentNote,
  firebaseToken,
  currentIndex,
  onNoteSelect,
  features,
  onDismiss,
  onSave,
  areaUnit,
  selectedNoteIds,
  toggleNoteSelected,
  allowEditingGeometry,
  setAllowEditingGeometry,
  showNoteGeometry,
  selectedProject,
  selectedFeatureIdParams,
}) => {
  const [noteWithEdits, setNoteWithEdits] = useStateWithDeps(currentNote, [currentNote.id]);

  // Ref for the current note in the queue for auto-scrolling
  const currentNoteRef = React.useRef<HTMLDivElement>(null);

  // Auto-scroll to current note when currentIndex changes
  React.useEffect(() => {
    if (currentNoteRef.current) {
      scrollToFocusedNote(currentNoteRef);
    }
  }, [currentIndex]);

  if (!noteWithEdits) {
    return null;
  }

  return (
    <>
      <NoteQueue
        noteQueue={noteQueue}
        currentIndex={currentIndex}
        onNoteSelect={onNoteSelect}
        features={features}
        currentNoteRef={currentNoteRef}
        noteWithEdits={noteWithEdits}
        setNoteWithEdits={setNoteWithEdits}
        onDismiss={onDismiss}
        onSave={onSave}
        areaUnit={areaUnit}
        selectedNoteIds={selectedNoteIds}
        toggleNoteSelected={toggleNoteSelected}
      />
      {noteWithEdits.geometry && noteWithEdits && (
        <ObservationMap
          allowEditingGeometry={allowEditingGeometry}
          setAllowEditingGeometry={setAllowEditingGeometry}
          selectedFeature={selectedFeature}
          selectedFeatureCollection={selectedFeatureCollection}
          organization={organization}
          imageRefs={makeImageRefsFromNote(noteWithEdits)}
          noteGeometry={noteWithEdits.geometry!}
          setNoteGeometry={(geometry) => setNoteWithEdits({...noteWithEdits, geometry})}
          firebaseToken={firebaseToken}
          showNoteGeometry={showNoteGeometry}
          selectedProject={selectedProject}
          selectedFeatureIdParams={selectedFeatureIdParams}
          refreshFeatureCollection={() => {}}
        />
      )}
    </>
  );
};

export const SelectedNoteReview: React.FC<{
  noteWithEdits: ApiAgentNote;
  setNoteWithEdits: (note: ApiAgentNote) => void;
  onDismiss: (noteWithEdits: ApiAgentNote) => void;
  onSave: (noteWithEdits: ApiAgentNote) => void;
}> = ({noteWithEdits, setNoteWithEdits, onDismiss, onSave}) => {
  const isSaved = isNoteSaved(noteWithEdits as unknown as ApiAgentNote);
  const isDismissed = isNoteDismissed(noteWithEdits as unknown as ApiAgentNote);

  return (
    <>
      <textarea
        className={cs.reviewObservationsTextArea}
        value={noteWithEdits.text}
        onChange={(e) => setNoteWithEdits({...noteWithEdits, text: e.target.value})}
        //make sure textarea is roughly the right size for a given note
        //guessing 48 chars per row
        rows={Math.max(3, Math.ceil(noteWithEdits.text.length / 36))}
      />

      <div className={cs.reviewObservationsActions}>
        <div style={{display: 'flex', gap: '1rem'}}>
          <B.Button
            small
            className={cs.dismissButton}
            onClick={(e) => {
              e.stopPropagation();

              onDismiss(noteWithEdits);
            }}
            outlined
            minimal
            disabled={isDismissed}
          >
            {isDismissed ? 'Dismissed!' : 'Dismiss'}
          </B.Button>
          <B.Button
            className={cs.agentAction}
            icon="clean"
            small
            onClick={(e) => {
              e.stopPropagation();

              onSave(noteWithEdits);
            }}
            intent="primary"
          >
            {isSaved ? 'Update' : isDismissed ? 'Restore' : 'Save as note'}
          </B.Button>
        </div>
      </div>
    </>
  );
};
